import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL } from "../../constant";
import {
  MdSearch,
  MdAssignmentReturn,
  MdCheckCircle,
  MdWarningAmber,
  MdArrowBack,
  MdReceiptLong,
} from "react-icons/md";
import "./RetailRefund.css";

function RetailRefund() {
  const outletContext = useOutletContext();
  const userData = outletContext?.userData;
  const token = localStorage.getItem("auth_token");

  // State
  const [invoiceNoInput, setInvoiceNoInput] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [searchError, setSearchError] = useState(null);

  // Refund Form State
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);

  // Processing State
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [completedRefund, setCompletedRefund] = useState(null);

  // Current Date and Time for receipt header (matching other pages)
  const now = new Date();
  const displayDate = now.toLocaleDateString("en-GB");
  const displayTime = now.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  // Auto-calculate breakdown when refundAmount changes
  useEffect(() => {
    if (!transactionData || !refundAmount) {
      setCalculationResult(null);
      return;
    }

    const numericAmount = parseFloat(refundAmount);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      setCalculationResult(null);
      return;
    }

    if (numericAmount > transactionData.remaining_refundable_amount + 0.001) {
      setCalculationResult(null);
      return;
    }

    const timer = setTimeout(() => {
      fetchCalculation(numericAmount);
    }, 350);

    return () => clearTimeout(timer);
  }, [refundAmount, transactionData]);

  // Handle invoice search
  const handleSearchInvoice = async (e) => {
    if (e) e.preventDefault();
    const cleanInvoice = invoiceNoInput.trim();
    if (!cleanInvoice) {
      toast.warning("Please enter an invoice number.");
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setTransactionData(null);
    setCalculationResult(null);
    setRefundAmount("");
    setRefundReason("");
    setCompletedRefund(null);

    try {
      const response = await fetch(
        `${API_URL}/api/retail-business/refund/lookup?invoice_no=${encodeURIComponent(
          cleanInvoice
        )}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      const resData = await response.json();

      if (response.ok && resData.success) {
        setTransactionData(resData.data);
        if (resData.data.is_fully_refunded) {
          toast.info("This invoice has already been fully refunded.");
        } else {
          toast.success("Invoice transaction found.");
        }
      } else {
        const errorMsg =
          resData.message || "Failed to find transaction for this invoice.";
        setSearchError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorMsg = "Network error while searching invoice. Please retry.";
      setSearchError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSearching(false);
    }
  };

  // Fetch calculation breakdown from backend
  const fetchCalculation = async (amount) => {
    if (!transactionData) return;
    setIsCalculating(true);

    try {
      const response = await fetch(
        `${API_URL}/api/retail-business/refund/calculate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
          body: JSON.stringify({
            invoice_no: transactionData.invoice_no,
            refund_amount: amount,
          }),
        }
      );

      const resData = await response.json();
      if (response.ok && resData.success) {
        setCalculationResult(resData.data);
      } else {
        setCalculationResult(null);
      }
    } catch (err) {
      setCalculationResult(null);
    } finally {
      setIsCalculating(false);
    }
  };

  // Quick preset button handler
  const handleQuickPreset = (fraction) => {
    if (!transactionData) return;
    const remaining = transactionData.remaining_refundable_amount;
    if (fraction === 1) {
      setRefundAmount(remaining.toFixed(2));
    } else {
      const amount = (remaining * fraction).toFixed(2);
      setRefundAmount(amount);
    }
  };

  // Process refund submission
  const handleProcessRefund = async () => {
    if (!transactionData || !calculationResult) return;
    const numericAmount = parseFloat(refundAmount);

    if (isNaN(numericAmount) || numericAmount <= 0) {
      toast.error("Please enter a valid refund amount.");
      return;
    }

    if (numericAmount > transactionData.remaining_refundable_amount + 0.001) {
      toast.error(
        `Refund amount cannot exceed remaining refundable amount (£${transactionData.remaining_refundable_amount.toFixed(
          2
        )}).`
      );
      return;
    }

    setShowConfirmModal(false);
    setIsProcessing(true);

    try {
      const response = await fetch(`${API_URL}/api/retail-business/refund/process`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({
          invoice_no: transactionData.invoice_no,
          refund_amount: numericAmount,
          reason: refundReason.trim() || "Customer Return",
        }),
      });

      const resData = await response.json();

      if (response.ok && resData.success) {
        toast.success(resData.message || "Refund processed successfully!");
        setCompletedRefund(resData.data);
      } else {
        toast.error(resData.message || "Failed to process refund.");
      }
    } catch (err) {
      toast.error("Network error while processing refund. Please check logs.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset to search another invoice
  const handleReset = () => {
    setInvoiceNoInput("");
    setTransactionData(null);
    setSearchError(null);
    setRefundAmount("");
    setRefundReason("");
    setCalculationResult(null);
    setCompletedRefund(null);
  };

  return (
    <div className="refund-outer">
      {/* Dark purple decorative border frame matching other pages */}
      <div className="refund-border-frame">
        {/* White receipt card */}
        <div className="refund-card">
          {/* Header matching other pages */}
          <div className="refund-header">
            <div className="refund-header-left">
              <h3>
                TopCoin UK
                <br />
                (TCUK)
              </h3>
            </div>
            <div className="refund-header-right">
              {displayDate} {displayTime}
            </div>
          </div>

          {/* Branding row matching other pages */}
          <div className="refund-branding">
            <p className="brand-name">TopCoinLand</p>
            <a
              className="brand-link"
              href="https://www.topcoinland.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.topcoinland.com
            </a>
            <div className="refund-page-badge">
              <MdAssignmentReturn /> Retail Refund
            </div>
          </div>

          {/* Success Screen */}
          {completedRefund ? (
            <div className="refund-success-receipt">
              <div className="refund-success-icon">
                <MdCheckCircle />
              </div>
              <h2 className="refund-success-title">Refund Completed Successfully!</h2>
              <p className="refund-success-subtitle">
                The distributed TopCoins have been clawed back from customer, admins, and
                agents, and credited back to your retail business wallet.
              </p>

              <div className="refund-receipt-details">
                <div className="refund-receipt-row">
                  <span>Refund Reference:</span>
                  <strong>{completedRefund.refund_ref}</strong>
                </div>
                <div className="refund-receipt-row">
                  <span>Invoice Number:</span>
                  <strong>{completedRefund.invoice_no}</strong>
                </div>
                <div className="refund-receipt-row">
                  <span>Refunded Amount:</span>
                  <strong style={{ color: "#b91c1c" }}>
                    £{parseFloat(completedRefund.refund_amount).toFixed(2)}
                  </strong>
                </div>
                <div className="refund-receipt-row">
                  <span>TopCoins Reclaimed to Wallet:</span>
                  <strong style={{ color: "#15803d", fontSize: "16px" }}>
                    +{completedRefund.total_reclaimed_topcoin} TC
                  </strong>
                </div>
                <div className="refund-receipt-row">
                  <span>Remaining Invoice Balance:</span>
                  <strong>
                    £{parseFloat(completedRefund.new_remaining_amount).toFixed(2)}
                  </strong>
                </div>
                <div className="refund-receipt-row">
                  <span>New Retail Wallet Balance:</span>
                  <strong style={{ color: "#701a75" }}>
                    {completedRefund.new_retail_wallet_balance} TC
                  </strong>
                </div>
              </div>

              <div className="refund-submit-area">
                <button
                  type="button"
                  className="refund-reset-btn"
                  onClick={handleReset}
                >
                  <MdArrowBack size={18} /> Process Another Refund
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Step 1: Invoice Search Form */}
              <form onSubmit={handleSearchInvoice} className="refund-search-card">
                <div className="refund-form-row">
                  <span className="label-text">Invoice Number:</span>
                  <div className="refund-search-input-wrapper">
                    <input
                      id="invoiceInput"
                      type="text"
                      className="field-input"
                      placeholder="e.g. INV-1002"
                      value={invoiceNoInput}
                      onChange={(e) => setInvoiceNoInput(e.target.value)}
                      disabled={isSearching || isProcessing}
                    />
                    <button
                      type="submit"
                      className="refund-search-btn"
                      disabled={isSearching || !invoiceNoInput.trim()}
                    >
                      <MdSearch size={18} />
                      {isSearching ? "Searching..." : "Lookup Invoice"}
                    </button>
                  </div>
                </div>

                {searchError && (
                  <div className="refund-error-banner">
                    <MdWarningAmber size={16} />
                    <span>{searchError}</span>
                  </div>
                )}
              </form>

              {/* Transaction Details (When Found) */}
              {transactionData && (
                <>
                  <div className="refund-details-panel">
                    <div className="refund-details-header">
                      <div>
                        <h4 className="refund-details-title">
                          Invoice: {transactionData.invoice_no}
                        </h4>
                        <div className="refund-sbc-ref">
                          SBC Ref: {transactionData.sbc_ref}
                        </div>
                      </div>
                      {/* <span
                        className={`refund-status-tag ${
                          transactionData.is_fully_refunded
                            ? "refund-status-refunded"
                            : transactionData.total_refunded_amount > 0
                              ? "refund-status-partially_refunded"
                              : "refund-status-completed"
                          }`}
                      >
                        {transactionData.is_fully_refunded
                          ? "Fully Refunded"
                          : transactionData.total_refunded_amount > 0
                          ? "Partially Refunded"
                          : "Active"}
                      </span> */}
                    </div>

                    {/* Customer & Transaction Meta */}
                    <div className="refund-summary-grid">
                      <div className="refund-summary-item">
                        <span className="label">Customer:</span>
                        <span className="value">
                          {transactionData.customer?.name} (
                          {transactionData.customer?.email})
                        </span>
                      </div>
                      <div className="refund-summary-item">
                        <span className="label">Transaction Date:</span>
                        <span className="value">
                          {new Date(
                            transactionData.transaction_datetime
                          ).toLocaleString("en-GB")}
                        </span>
                      </div>
                    </div>

                    {/* Awarding-style Rows matching RetailTcDistribution */}
                    <div className="refund-award-row">
                      <span className="label-text">Original Total:</span>
                      <span className="award-value">
                        £
                        {parseFloat(
                          transactionData.original_transaction_total
                        ).toFixed(2)}
                      </span>
                    </div>

                    <div className="refund-award-row">
                      <span className="label-text">Total Refunded:</span>
                      <span className="award-value" style={{ color: "#b91c1c" }}>
                        £
                        {parseFloat(
                          transactionData.total_refunded_amount
                        ).toFixed(2)}
                      </span>
                    </div>

                    <div className="refund-award-row highlight-green">
                      <span className="label-text">Remaining Refundable:</span>
                      <span className="award-value">
                        £
                        {parseFloat(
                          transactionData.remaining_refundable_amount
                        ).toFixed(2)}
                      </span>
                    </div>

                    <div className="refund-award-row">
                      <span className="label-text">Awarded TopCoins:</span>
                      <span className="award-value">
                        {transactionData.original_awarding_topcoin} TC
                        {/* <span className="sub-tc">
                          ({transactionData.remaining_refundable_topcoin} TC
                          remaining)
                        </span> */}
                      </span>
                    </div>
                  </div>

                  {/* Refund Input & Breakdown Form */}
                  {transactionData.is_fully_refunded ? (
                    <div
                      style={{
                        padding: "16px",
                        background: "#fef2f2",
                        border: "1px solid #fca5a5",
                        borderRadius: "6px",
                        textAlign: "center",
                        color: "#991b1b",
                        fontWeight: "700",
                        fontSize: "14px",
                      }}
                    >
                      This invoice has already been fully refunded. No further
                      refunds can be processed.
                    </div>
                  ) : (
                    <div className="refund-form-section">
                      {/* <h4 className="refund-step-title">
                        <MdReceiptLong size={18} />
                        Step 2: Enter Refund Amount
                      </h4> */}

                      {/* Refund Amount Row */}
                      <div className="refund-form-row">
                        <span className="label-text">
                          Refund Transaction Amount (£):
                        </span>
                        <div className="refund-amount-input-wrapper">
                          <input
                            id="refundAmountInput"
                            type="number"
                            step="0.01"
                            min="0.01"
                            max={transactionData.remaining_refundable_amount}
                            className="field-input currency-input"
                            placeholder={`Max £${transactionData.remaining_refundable_amount.toFixed(
                              2
                            )}`}
                            value={refundAmount}
                            onChange={(e) => setRefundAmount(e.target.value)}
                          />

                          {/* Quick Presets */}
                          {/* <div className="refund-quick-chips">
                            <button
                              type="button"
                              className="refund-chip-btn"
                              onClick={() => handleQuickPreset(1)}
                            >
                              Full Refund (£
                              {transactionData.remaining_refundable_amount.toFixed(
                                2
                              )}
                              )
                            </button>
                            <button
                              type="button"
                              className="refund-chip-btn"
                              onClick={() => handleQuickPreset(0.5)}
                            >
                              50%
                            </button>
                            <button
                              type="button"
                              className="refund-chip-btn"
                              onClick={() => handleQuickPreset(0.25)}
                            >
                              25%
                            </button>
                          </div> */}
                        </div>
                      </div>

                      {/* Reason Row */}
                      {/* <div className="refund-form-row">
                        <span className="label-text">
                          Reason for Refund (Optional):
                        </span>
                        <input
                          id="reasonInput"
                          type="text"
                          className="field-input"
                          placeholder="e.g. Returned items, Damaged goods"
                          value={refundReason}
                          onChange={(e) => setRefundReason(e.target.value)}
                        />
                      </div> */}

                      {/* Live Calculation Indicator */}
                      {/* {isCalculating && (
                        <div className="refund-calculating-text">
                          Calculating clawback values...
                        </div>
                      )} */}

                      {/* Clawback Breakdown */}
                      {calculationResult && (
                        <div className="refund-breakdown-card">
                          {/* <div className="refund-breakdown-header">
                            <span>Clawback & Reversal Breakdown</span>
                            <span className="badge-tc">
                              Base Clawback:{" "}
                              {calculationResult.base_refund_topcoin} TC
                            </span>
                          </div> */}

                          <div className="refund-breakdown-items">
                            {/* {calculationResult.reversal_breakdown
                              ?.filter((item) => item.action === "clawback")
                              .map((item, idx) => (
                                <div
                                  key={idx}
                                  className="refund-breakdown-item"
                                >
                                  <span className="entity-label">
                                    {item.entity}: {item.name}
                                  </span>
                                  <span className="clawback-val">
                                    -{item.topcoin_clawback} TC
                                    {item.bonus_clawback_gbp > 0 &&
                                      ` (-£${item.bonus_clawback_gbp.toFixed(
                                        2
                                      )} bonus)`}
                                  </span>
                                </div>
                              ))} */}
                          </div>

                          {/* Highlight box for retail business credit */}
                          <div className="refund-credit-banner">
                            <span>TOTAL CREDITED TO RETAIL BUSINESS:</span>
                            <span className="credit-tc">
                              +{calculationResult.total_reclaimed_topcoin} TC
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Submit Area matching other pages */}
                      <div className="refund-submit-area">
                        <button
                          type="button"
                          className="refund-submit-btn"
                          disabled={
                            isProcessing ||
                            !calculationResult ||
                            parseFloat(refundAmount) <= 0 ||
                            parseFloat(refundAmount) >
                              transactionData.remaining_refundable_amount +
                                0.001
                          }
                          onClick={() => setShowConfirmModal(true)}
                        >
                          <MdAssignmentReturn size={18} />
                          {isProcessing
                            ? "PROCESSING REFUND..."
                            : `CONFIRM & PROCESS REFUND (£${parseFloat(
                              refundAmount || 0
                            ).toFixed(2)})`}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Past Refunds on this Invoice */}
                  {transactionData.past_refunds?.length > 0 && (
                    <div className="refund-history-section">
                      <h4 className="refund-history-title">
                        Past Refunds for Invoice {transactionData.invoice_no} (
                        {transactionData.past_refunds.length})
                      </h4>
                      <div className="refund-history-table-wrapper">
                        <table className="refund-history-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Refund Ref</th>
                              <th>Amount</th>
                              <th>Reclaimed TC</th>
                              <th>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactionData.past_refunds.map((refItem) => (
                              <tr key={refItem.id}>
                                <td>
                                  {new Date(
                                    refItem.created_at
                                  ).toLocaleDateString("en-GB")}
                                </td>
                                <td style={{ fontFamily: "monospace" }}>
                                  {refItem.refund_ref}
                                </td>
                                <td
                                  style={{
                                    color: "#b91c1c",
                                    fontWeight: "700",
                                  }}
                                >
                                  £
                                  {parseFloat(refItem.refund_amount).toFixed(2)}
                                </td>
                                <td
                                  style={{
                                    color: "#15803d",
                                    fontWeight: "700",
                                  }}
                                >
                                  +{refItem.total_reclaimed_topcoin} TC
                                </td>
                                <td>{refItem.reason || "N/A"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Confirmation Modal */}
          {showConfirmModal && calculationResult && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
                padding: "16px",
              }}
            >
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: "8px",
                  maxWidth: "460px",
                  width: "100%",
                  padding: "24px",
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
                  boxSizing: "border-box",
                }}
              >
                <div style={{ textAlign: "center", marginBottom: "16px" }}>
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      background: "#fee2e2",
                      color: "#dc2626",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 12px",
                      fontSize: "24px",
                    }}
                  >
                    <MdWarningAmber />
                  </div>
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "800",
                      color: "#1e293b",
                      margin: "0 0 6px",
                    }}
                  >
                    Confirm Refund Execution
                  </h3>
                  <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>
                    Are you sure you want to process this refund?
                  </p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    padding: "12px 16px",
                    fontSize: "13px",
                    marginBottom: "18px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "6px",
                    }}
                  >
                    <span>Invoice:</span>
                    <strong>{transactionData.invoice_no}</strong>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "6px",
                    }}
                  >
                    <span>Refund Amount:</span>
                    <strong style={{ color: "#b91c1c" }}>
                      £{parseFloat(refundAmount).toFixed(2)}
                    </strong>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      borderTop: "1px dashed #cbd5e1",
                      paddingTop: "6px",
                    }}
                  >
                    <span>Reclaimed TopCoins:</span>
                    <strong style={{ color: "#15803d", fontSize: "14px" }}>
                      +{calculationResult.total_reclaimed_topcoin} TC
                    </strong>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    style={{
                      flex: 1,
                      background: "#f1f5f9",
                      color: "#334155",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      padding: "11px 18px",
                      fontSize: "14px",
                      fontWeight: "700",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowConfirmModal(false)}
                    disabled={isProcessing}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{
                      flex: 1,
                      background:
                        "linear-gradient(135deg, #4d0f46 0%, #701a75 100%)",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "4px",
                      padding: "11px 18px",
                      fontSize: "14px",
                      fontWeight: "800",
                      cursor: "pointer",
                    }}
                    onClick={handleProcessRefund}
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Processing..." : "Yes, Process"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RetailRefund;